import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../actions/api"; // Reuse your Axios instance
import { showLoader, hideLoader } from "./loaderSlice";
import { showSnackBar } from "./snackSlice";

// GET all templates
export const getTemplates = createAsyncThunk(
  "template/getTemplates",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get("/getall/clinic/templates");
      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res.data.data;
      }
      thunkAPI.dispatch(hideLoader());
      return [];
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(showSnackBar({ message: "Failed to fetch templates", type: "error" }));
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

// POST create template
export const createTemplate = createAsyncThunk(
  "template/createTemplate",
  async (formData, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post("/create/clinic/template", formData);
      if (res.status === 200 || res.status === 201) {
        thunkAPI.dispatch(hideLoader());
        thunkAPI.dispatch(showSnackBar({ message: "Template created successfully!", type: "success" }));
        return res.data.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to create template");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(showSnackBar({ message: "Failed to create template", type: "error" }));
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

// PUT update template
export const updateTemplate = createAsyncThunk(
  "template/updateTemplate",
  async ({ id, ...formData }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(`/update/clinic/template/${id}`, formData);
      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        thunkAPI.dispatch(showSnackBar({ message: "Template updated successfully!", type: "success" }));
        return res.data.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to update template");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(showSnackBar({ message: "Failed to update template", type: "error" }));
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

// DELETE template
export const deleteTemplate = createAsyncThunk(
  "template/deleteTemplate",
  async (id, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).delete(`/delete/clinic/template/${id}`);
      if (res.status === 200 || res.status === 204) {
        thunkAPI.dispatch(hideLoader());
        thunkAPI.dispatch(showSnackBar({ message: "Template deleted successfully!", type: "success" }));
        return id;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to delete template");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      thunkAPI.dispatch(showSnackBar({ message: "Failed to delete template", type: "error" }));
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  error: null,
};

const templateSlice = createSlice({
  name: "template",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.list = Array.isArray(action.payload) ? action.payload : [action.payload];
      })
      .addCase(getTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.list.push(action.payload);
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(t => t.templateId === action.payload.templateId);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(t => t.templateId !== action.payload);
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default templateSlice.reducer;

